import React, { useState, useEffect } from 'react';
import { getTirages, getTirage, getProjets, getAvailableParcelles, getAvailableMembres, getDirections, executeTirage, deleteTirage } from '../services/api';
import { FiPlus, FiTrash2, FiX, FiEye, FiShuffle, FiCheck, FiFilter, FiArrowRight, FiArrowLeft, FiUsers, FiGrid, FiSearch } from 'react-icons/fi';

const Tirages = () => {
  const [tirages, setTirages] = useState<any[]>([]);
  const [projets, setProjets] = useState<any[]>([]);
  const [directions, setDirections] = useState<any[]>([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // New tirage wizard
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1); // 1=config, 2=select, 3=confirm, 4=result
  const [wizardForm, setWizardForm] = useState({ libelle: '', projet_id: '', description: '' });
  const [availableParcelles, setAvailableParcelles] = useState<any[]>([]);
  const [availableMembres, setAvailableMembres] = useState<any[]>([]);
  const [selectedParcelles, setSelectedParcelles] = useState<string[]>([]);
  const [selectedMembres, setSelectedMembres] = useState<string[]>([]);
  const [directionFilter, setDirectionFilter] = useState('');
  const [parcelleSearch, setParcelleSearch] = useState('');
  const [membreSearch, setMembreSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [tirageResult, setTirageResult] = useState<any>(null);
  const [executing, setExecuting] = useState(false);

  // Detail modal
  const [showDetail, setShowDetail] = useState(false);
  const [detailData, setDetailData] = useState<any>(null);

  const loadData = () => {
    getTirages().then(r => setTirages(r.data)).catch(() => {});
  };

  useEffect(() => { loadData(); }, []);
  useEffect(() => {
    getProjets().then(r => setProjets(r.data)).catch(() => {});
    getDirections().then(r => setDirections(r.data)).catch(() => {});
  }, []);

  // Load available parcelles & members when project changes
  const loadAvailableData = async (projetId: string, dirId?: string) => {
    if (!projetId) return;
    setLoading(true);
    try {
      const [pRes, mRes] = await Promise.all([
        getAvailableParcelles(projetId),
        getAvailableMembres(projetId, dirId)
      ]);
      setAvailableParcelles(pRes.data);
      setAvailableMembres(mRes.data);
      setSelectedParcelles(pRes.data.map((p: any) => p.id)); // select all by default
      setSelectedMembres(mRes.data.map((m: any) => m.id)); // select all by default
    } catch {
      setAvailableParcelles([]);
      setAvailableMembres([]);
    }
    setLoading(false);
  };

  const openWizard = () => {
    setWizardForm({ libelle: '', projet_id: '', description: '' });
    setWizardStep(1);
    setAvailableParcelles([]);
    setAvailableMembres([]);
    setSelectedParcelles([]);
    setSelectedMembres([]);
    setDirectionFilter('');
    setParcelleSearch('');
    setMembreSearch('');
    setTirageResult(null);
    setShowWizard(true);
  };

  const handleProjectChange = (projetId: string) => {
    setWizardForm({ ...wizardForm, projet_id: projetId });
    if (projetId) loadAvailableData(projetId);
  };

  const handleDirectionFilterChange = (dirId: string) => {
    setDirectionFilter(dirId);
    if (wizardForm.projet_id) loadAvailableData(wizardForm.projet_id, dirId);
  };

  const toggleParcelle = (id: string) => {
    setSelectedParcelles(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleMembre = (id: string) => {
    setSelectedMembres(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleAllParcelles = () => {
    const filtered = filteredParcelles;
    const allSelected = filtered.every(p => selectedParcelles.includes(p.id));
    if (allSelected) {
      setSelectedParcelles(prev => prev.filter(id => !filtered.find(p => p.id === id)));
    } else {
      setSelectedParcelles(prev => {
        const ids = filtered.map(p => p.id);
        const merged = [...prev];
        ids.forEach(id => { if (!merged.includes(id)) merged.push(id); });
        return merged;
      });
    }
  };
  const toggleAllMembres = () => {
    const filtered = filteredMembres;
    const allSelected = filtered.every(m => selectedMembres.includes(m.id));
    if (allSelected) {
      setSelectedMembres(prev => prev.filter(id => !filtered.find(m => m.id === id)));
    } else {
      setSelectedMembres(prev => {
        const ids = filtered.map(m => m.id);
        const merged = [...prev];
        ids.forEach(id => { if (!merged.includes(id)) merged.push(id); });
        return merged;
      });
    }
  };

  const filteredParcelles = availableParcelles.filter(p =>
    !parcelleSearch || p.numero?.toString().includes(parcelleSearch) || p.lot?.toString().includes(parcelleSearch)
  );
  const filteredMembres = availableMembres.filter(m =>
    !membreSearch || (m.prenomnom || '').toLowerCase().includes(membreSearch.toLowerCase()) || (m.matricule || '').includes(membreSearch)
  );

  const handleExecute = async () => {
    if (!wizardForm.libelle || !wizardForm.projet_id) {
      setError('Libellé et projet requis');
      setTimeout(() => setError(''), 3000);
      return;
    }
    if (!selectedParcelles.length || !selectedMembres.length) {
      setError('Sélectionnez au moins une parcelle et un membre');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setExecuting(true);
    try {
      const res = await executeTirage({
        libelle: wizardForm.libelle,
        projet_id: wizardForm.projet_id,
        description: wizardForm.description,
        parcelle_ids: selectedParcelles,
        membre_ids: selectedMembres
      });
      setTirageResult(res.data);
      setWizardStep(4);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors du tirage');
      setTimeout(() => setError(''), 5000);
    }
    setExecuting(false);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer ce tirage ?\nToutes les attributions effectuées par ce tirage seront annulées.')) return;
    try {
      await deleteTirage(id);
      setSuccess('Tirage supprimé et attributions annulées');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur');
      setTimeout(() => setError(''), 3000);
    }
  };

  const openDetail = async (id: string) => {
    try {
      const res = await getTirage(id);
      setDetailData(res.data);
      setShowDetail(true);
    } catch {
      setError('Erreur lors du chargement');
      setTimeout(() => setError(''), 3000);
    }
  };

  const nbAttributions = Math.min(selectedParcelles.length, selectedMembres.length);
  const projetName = projets.find(p => p.id === wizardForm.projet_id)?.libelle || '';

  return (
    <div>
      <h1 className="page-title">Tirages au sort</h1>
      <p style={{ color: '#888', fontSize: '0.9rem', marginTop: -8, marginBottom: 16 }}>
        Attribution aléatoire des parcelles aux membres par tirage au sort.
      </p>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Tirages list */}
      <div className="card">
        <div className="card-header">
          <span>Tirages ({tirages.length})</span>
          <button className="btn btn-success" onClick={openWizard}><FiShuffle /> Nouveau tirage</button>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Libellé</th>
                  <th>Projet</th>
                  <th>Attributions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tirages.map(t => (
                  <tr key={t.id}>
                    <td>{t.datetirage ? new Date(t.datetirage).toLocaleDateString('fr-FR') : t.created ? new Date(t.created).toLocaleDateString('fr-FR') : '-'}</td>
                    <td><strong>{t.libelle || '-'}</strong></td>
                    <td>{t.projet_libelle ? <span className="badge badge-blue">{t.projet_libelle}</span> : '-'}</td>
                    <td>
                      <span className="badge" style={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }}>
                        {t.nb_attributions || 0} parcelle(s)
                      </span>
                    </td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon" style={{ color: '#1565C0' }} onClick={() => openDetail(t.id)} title="Voir détails"><FiEye /></button>
                        <button className="btn-icon btn-danger" onClick={() => handleDelete(t.id)} title="Supprimer et annuler les attributions"><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {tirages.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>Aucun tirage effectué</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ===== WIZARD MODAL ===== */}
      {showWizard && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: wizardStep >= 2 ? '1000px' : '600px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {wizardStep === 1 && 'Nouveau tirage — Configuration'}
                {wizardStep === 2 && 'Sélection des parcelles et membres'}
                {wizardStep === 3 && 'Confirmation du tirage'}
                {wizardStep === 4 && 'Résultat du tirage'}
              </h3>
              <button className="modal-close" onClick={() => setShowWizard(false)}><FiX /></button>
            </div>

            {/* Step indicators */}
            <div style={{ display: 'flex', gap: 4, padding: '0 24px', marginBottom: 8 }}>
              {[1, 2, 3, 4].map(s => (
                <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: s <= wizardStep ? '#1565C0' : '#e0e0e0', transition: 'background 0.3s' }} />
              ))}
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px 24px' }}>
              {/* STEP 1: Config */}
              {wizardStep === 1 && (
                <div>
                  <div className="form-group">
                    <label>Libellé du tirage *</label>
                    <input className="form-input" value={wizardForm.libelle} onChange={e => setWizardForm({ ...wizardForm, libelle: e.target.value })} placeholder="Ex: Tirage Phase 1 - Avril 2026" required />
                  </div>
                  <div className="form-group">
                    <label>Projet *</label>
                    <select className="form-input" value={wizardForm.projet_id} onChange={e => handleProjectChange(e.target.value)} required>
                      <option value="">-- Sélectionner un projet --</option>
                      {projets.map(p => <option key={p.id} value={p.id}>{p.libelle}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Description (optionnel)</label>
                    <textarea className="form-input" value={wizardForm.description} onChange={e => setWizardForm({ ...wizardForm, description: e.target.value })} rows={3} placeholder="Notes sur ce tirage..." />
                  </div>

                  {wizardForm.projet_id && !loading && (
                    <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
                      <div style={{ flex: 1, padding: 16, background: '#e3f2fd', borderRadius: 10, textAlign: 'center' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1565C0' }}>{availableParcelles.length}</div>
                        <div style={{ fontSize: '0.82rem', color: '#555' }}>Parcelles disponibles</div>
                      </div>
                      <div style={{ flex: 1, padding: 16, background: '#e8f5e9', borderRadius: 10, textAlign: 'center' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#2e7d32' }}>{availableMembres.length}</div>
                        <div style={{ fontSize: '0.82rem', color: '#555' }}>Membres éligibles</div>
                      </div>
                    </div>
                  )}
                  {loading && <div style={{ textAlign: 'center', padding: 20, color: '#888' }}>Chargement...</div>}
                </div>
              )}

              {/* STEP 2: Selection */}
              {wizardStep === 2 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  {/* Parcelles panel */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}><FiGrid /> Parcelles</h4>
                      <span className="badge" style={{ background: '#e3f2fd', color: '#1565C0' }}>{selectedParcelles.length}/{availableParcelles.length}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <input className="form-input" placeholder="Rechercher n°..." value={parcelleSearch} onChange={e => setParcelleSearch(e.target.value)} style={{ paddingLeft: 32, fontSize: '0.82rem' }} />
                        <FiSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: '0.85rem' }} />
                      </div>
                      <button type="button" className="btn btn-outline" style={{ fontSize: '0.75rem', padding: '4px 10px' }} onClick={toggleAllParcelles}>
                        {filteredParcelles.every(p => selectedParcelles.includes(p.id)) ? 'Désélect.' : 'Tout'}
                      </button>
                    </div>
                    <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 8 }}>
                      {filteredParcelles.map(p => (
                        <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid #f5f5f5', cursor: 'pointer', background: selectedParcelles.includes(p.id) ? '#f0f7ff' : 'transparent', fontSize: '0.85rem' }}>
                          <input type="checkbox" checked={selectedParcelles.includes(p.id)} onChange={() => toggleParcelle(p.id)} />
                          <span style={{ fontWeight: 600 }}>N° {p.numero}</span>
                          <span style={{ color: '#888', marginLeft: 'auto', fontSize: '0.78rem' }}>{p.superficie} m²</span>
                        </label>
                      ))}
                      {filteredParcelles.length === 0 && <div style={{ padding: 16, textAlign: 'center', color: '#999', fontSize: '0.85rem' }}>Aucune parcelle</div>}
                    </div>
                  </div>

                  {/* Membres panel */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}><FiUsers /> Membres</h4>
                      <span className="badge" style={{ background: '#e8f5e9', color: '#2e7d32' }}>{selectedMembres.length}/{availableMembres.length}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <input className="form-input" placeholder="Rechercher nom/matricule..." value={membreSearch} onChange={e => setMembreSearch(e.target.value)} style={{ paddingLeft: 32, fontSize: '0.82rem' }} />
                        <FiSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: '0.85rem' }} />
                      </div>
                      <button type="button" className="btn btn-outline" style={{ fontSize: '0.75rem', padding: '4px 10px' }} onClick={toggleAllMembres}>
                        {filteredMembres.every(m => selectedMembres.includes(m.id)) ? 'Désélect.' : 'Tout'}
                      </button>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <select className="form-input" value={directionFilter} onChange={e => handleDirectionFilterChange(e.target.value)} style={{ fontSize: '0.82rem' }}>
                        <option value="">Toutes les directions</option>
                        {directions.map(d => <option key={d.id} value={d.id}>{d.libelle}</option>)}
                      </select>
                    </div>
                    <div style={{ maxHeight: 360, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 8 }}>
                      {filteredMembres.map(m => (
                        <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid #f5f5f5', cursor: 'pointer', background: selectedMembres.includes(m.id) ? '#f0fff4' : 'transparent', fontSize: '0.85rem' }}>
                          <input type="checkbox" checked={selectedMembres.includes(m.id)} onChange={() => toggleMembre(m.id)} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600 }}>{m.prenomnom}</div>
                            <div style={{ fontSize: '0.75rem', color: '#888' }}>{m.matricule}{m.direction_libelle ? ` — ${m.direction_libelle}` : ''}</div>
                          </div>
                        </label>
                      ))}
                      {filteredMembres.length === 0 && <div style={{ padding: 16, textAlign: 'center', color: '#999', fontSize: '0.85rem' }}>Aucun membre</div>}
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3: Confirmation */}
              {wizardStep === 3 && (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: '3rem', marginBottom: 12 }}><FiShuffle /></div>
                  <h2 style={{ fontSize: '1.3rem', color: '#1a1a2e', marginBottom: 8 }}>{wizardForm.libelle}</h2>
                  <p style={{ color: '#888', marginBottom: 24 }}>Projet : <strong>{projetName}</strong></p>

                  <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginBottom: 24 }}>
                    <div style={{ padding: '16px 32px', background: '#e3f2fd', borderRadius: 12 }}>
                      <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#1565C0' }}>{selectedParcelles.length}</div>
                      <div style={{ fontSize: '0.82rem', color: '#555' }}>Parcelles</div>
                    </div>
                    <div style={{ padding: '16px 32px', background: '#e8f5e9', borderRadius: 12 }}>
                      <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#2e7d32' }}>{selectedMembres.length}</div>
                      <div style={{ fontSize: '0.82rem', color: '#555' }}>Membres</div>
                    </div>
                    <div style={{ padding: '16px 32px', background: '#fff3e0', borderRadius: 12 }}>
                      <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#e65100' }}>{nbAttributions}</div>
                      <div style={{ fontSize: '0.82rem', color: '#555' }}>Attributions</div>
                    </div>
                  </div>

                  {selectedParcelles.length !== selectedMembres.length && (
                    <div className="alert" style={{ background: '#fff3e0', color: '#e65100', border: '1px solid #ffe0b2', marginBottom: 16 }}>
                      {selectedMembres.length > selectedParcelles.length
                        ? `${selectedMembres.length - selectedParcelles.length} membre(s) ne recevront pas de parcelle (plus de membres que de parcelles)`
                        : `${selectedParcelles.length - selectedMembres.length} parcelle(s) resteront non attribuées (plus de parcelles que de membres)`
                      }
                    </div>
                  )}

                  <p style={{ color: '#c62828', fontWeight: 600, fontSize: '0.9rem' }}>
                    Le tirage est aléatoire et irréversible (sauf suppression du tirage).
                  </p>
                </div>
              )}

              {/* STEP 4: Results */}
              {wizardStep === 4 && tirageResult && (
                <div>
                  <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: '1.5rem', color: '#2e7d32' }}><FiCheck /></div>
                    <h2 style={{ fontSize: '1.3rem', color: '#2e7d32', marginBottom: 4 }}>Tirage effectué avec succès !</h2>
                    <p style={{ color: '#888' }}>{tirageResult.nb_attributions} attribution(s) réalisée(s)</p>
                  </div>

                  <div className="table-responsive">
                    <table className="data-table" style={{ fontSize: '0.85rem' }}>
                      <thead>
                        <tr><th>#</th><th>Parcelle</th><th>Membre attribué</th></tr>
                      </thead>
                      <tbody>
                        {tirageResult.attributions?.map((a: any, i: number) => {
                          const parc = availableParcelles.find(p => p.id === a.parcelle_id);
                          return (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td><strong>N° {parc?.numero || '?'}</strong> <span style={{ color: '#888', fontSize: '0.78rem' }}>({parc?.superficie || '?'} m²)</span></td>
                              <td>{a.prenomnom || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Footer navigation */}
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                {wizardStep > 1 && wizardStep < 4 && (
                  <button type="button" className="btn btn-outline" onClick={() => setWizardStep(wizardStep - 1)}>
                    <FiArrowLeft /> Précédent
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {wizardStep < 4 && (
                  <button type="button" className="btn btn-outline" onClick={() => setShowWizard(false)}>Annuler</button>
                )}
                {wizardStep === 1 && (
                  <button type="button" className="btn btn-primary" disabled={!wizardForm.libelle || !wizardForm.projet_id || loading}
                    onClick={() => setWizardStep(2)}>
                    Suivant <FiArrowRight />
                  </button>
                )}
                {wizardStep === 2 && (
                  <button type="button" className="btn btn-primary" disabled={!selectedParcelles.length || !selectedMembres.length}
                    onClick={() => setWizardStep(3)}>
                    Suivant <FiArrowRight />
                  </button>
                )}
                {wizardStep === 3 && (
                  <button type="button" className="btn btn-success" disabled={executing} onClick={handleExecute}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FiShuffle /> {executing ? 'Tirage en cours...' : 'Lancer le tirage'}
                  </button>
                )}
                {wizardStep === 4 && (
                  <button type="button" className="btn btn-primary" onClick={() => setShowWizard(false)}>Fermer</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== DETAIL MODAL ===== */}
      {showDetail && detailData && (
        <div className="modal-overlay" onClick={() => setShowDetail(false)}>
          <div className="modal" style={{ maxWidth: '750px', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Détails du tirage</h3>
              <button className="modal-close" onClick={() => setShowDetail(false)}><FiX /></button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px 24px' }}>
              <div style={{ marginBottom: 16 }}>
                <h2 style={{ fontSize: '1.2rem', color: '#1a1a2e', marginBottom: 4 }}>{detailData.libelle}</h2>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                  <span className="badge badge-blue">{detailData.projet_libelle}</span>
                  <span style={{ color: '#888', fontSize: '0.85rem' }}>
                    {detailData.datetirage ? new Date(detailData.datetirage).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                  </span>
                </div>
                {detailData.description && <p style={{ color: '#666', fontSize: '0.9rem' }}>{detailData.description}</p>}
                <div className="badge" style={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: '0.85rem' }}>
                  {detailData.attributions?.length || 0} attribution(s)
                </div>
              </div>

              <div className="table-responsive">
                <table className="data-table" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr><th>#</th><th>Parcelle</th><th>Superficie</th><th>Membre</th><th>Matricule</th></tr>
                  </thead>
                  <tbody>
                    {detailData.attributions?.map((a: any, i: number) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td><strong>N° {a.numero}</strong></td>
                        <td>{a.superficie ? `${a.superficie} m²` : '-'}</td>
                        <td>{a.membre_nom || '-'}</td>
                        <td style={{ fontSize: '0.8rem', color: '#888' }}>{a.membre_matricule || '-'}</td>
                      </tr>
                    ))}
                    {(!detailData.attributions || detailData.attributions.length === 0) && (
                      <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1.5rem', color: '#999' }}>Aucune attribution</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tirages;
